import { Link, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { TabNav } from "../../components/TabNav";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useLumiConfig, useUpdateLumiConfig } from "../../lib/queries";
import { LetterheadCard, QuoteSettingsCard } from "../quotes/QuoteSettingsCard";
import { CompanyCard } from "./CompanyCard";
import { ScheduleCard } from "./ScheduleCard";

/** The company's profile, its hours, its locations and its quote settings share the Empresa section. */
export function CompanyTabs() {
  const tab = useSearch({ strict: false }) as { tab?: string };
  return (
    <TabNav label="Empresa">
      <Link
        to="/empresa"
        search={{}}
        activeOptions={{ exact: true, includeSearch: true }}
        activeProps={{ "aria-current": "page" }}
      >
        Perfil
      </Link>
      <Link to="/empresa" search={{ tab: "horario" }} aria-current={tab.tab === "horario" ? "page" : undefined}>
        Horario
      </Link>
      <Link to="/empresa/puntos" activeProps={{ "aria-current": "page" }}>
        Ubicaciones
      </Link>
      <Link
        to="/empresa"
        search={{ tab: "cotizaciones" }}
        aria-current={tab.tab === "cotizaciones" ? "page" : undefined}
      >
        Cotizaciones
      </Link>
    </TabNav>
  );
}

/** Who the company is, how to reach it and when it is open: Lumi and the templates use it. */
export function CompanyPage() {
  const { tab } = useSearch({ from: "/app/empresa" });
  return (
    <div className="page">
      <div className="page-head">
        <h1>Empresa</h1>
      </div>
      <CompanyTabs />
      {tab === "horario" ? (
        <ScheduleCard />
      ) : tab === "cotizaciones" ? (
        <>
          <QuoteSettingsCard />
          <LetterheadCard />
        </>
      ) : (
        <>
          <DescriptionCard />
          <CompanyCard />
        </>
      )}
    </div>
  );
}

/** What the company does and for whom: Lumi introduces it and knows what to talk about. */
function DescriptionCard() {
  const config = useLumiConfig();
  const update = useUpdateLumiConfig();
  const toast = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  const saved = config.data?.businessDescription ?? "";
  const value = draft ?? saved;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ businessDescription: value });
      setDraft(null);
      toast("Descripción guardada");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar.");
    }
  }

  return (
    <div className="card">
      <form className="card-body" onSubmit={onSubmit}>
        <div>
          <h3>Qué hace la empresa</h3>
          <p className="muted">Lumi lo usa para presentarse y para saber de qué puede hablar.</p>
        </div>
        <textarea
          className="textarea"
          aria-label="Qué hace la empresa y para quién"
          rows={4}
          maxLength={2000}
          placeholder="Ej.: Distribuidora de electrodomésticos y plantillas de gas para hogares y restaurantes en todo Costa Rica."
          value={value}
          disabled={config.isPending}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={draft === null || draft === saved || update.isPending}
          >
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}
